import React, { useState } from 'react';
import { useOtelMetrics } from '../../hooks/useOtelSpans';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { Activity, Clock, AlertTriangle, AlertCircle } from 'lucide-react';
import { Alert, AlertDescription } from '../ui/alert';

export const PerformanceMetrics = ({ autoRefresh }) => {
  const [hours, setHours] = useState(24);

  const { data, isLoading, isError } = useOtelMetrics({ hours }, { autoRefresh });

  // Prepare latency percentile data
  const latencyData = data?.latency ? [
    { name: 'P50', value: data.latency.p50 },
    { name: 'P95', value: data.latency.p95 },
    { name: 'P99', value: data.latency.p99 },
    { name: 'Avg', value: data.latency.avg },
    { name: 'Max', value: data.latency.max },
  ] : [];

  return (
    <div className="space-y-6">
      {/* Controls */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">Performance Metrics</h2>
        <div className="flex items-center gap-4">
          <label className="text-sm font-medium">Time Range:</label>
          <Select
            value={hours.toString()}
            onValueChange={(value) => setHours(parseInt(value))}
          >
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1">Last Hour</SelectItem>
              <SelectItem value="6">Last 6 Hours</SelectItem>
              <SelectItem value="24">Last 24 Hours</SelectItem>
              <SelectItem value="168">Last Week</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {isError && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Failed to load performance metrics. Please check your backend connection.
          </AlertDescription>
        </Alert>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">P50 Latency</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{data?.latency?.p50 || 0}ms</div>
            <p className="text-xs text-muted-foreground">Median response time</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">P95 Latency</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{data?.latency?.p95 || 0}ms</div>
            <p className="text-xs text-muted-foreground">95th percentile</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg Latency</CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{data?.latency?.avg?.toFixed(0) || 0}ms</div>
            <p className="text-xs text-muted-foreground">Average response time</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Error Rate</CardTitle>
            <AlertTriangle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{data?.error_rate?.toFixed(1) || 0}%</div>
            <p className="text-xs text-muted-foreground">
              {data?.error_count || 0} errors out of {data?.total_spans || 0} calls
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Latency Distribution Chart */}
      <Card>
        <CardHeader>
          <CardTitle>Latency Distribution</CardTitle>
          <CardDescription>Response time percentiles in milliseconds</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="h-80 flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : latencyData.length === 0 ? (
            <div className="h-80 flex items-center justify-center text-muted-foreground">
              No latency data available
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={latencyData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis label={{ value: 'Latency (ms)', angle: -90, position: 'insideLeft' }} />
                <Tooltip formatter={(value) => `${value}ms`} />
                <Legend />
                <Bar dataKey="value" fill="#3b82f6" name="Latency (ms)" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      {/* Detailed Latency Stats */}
      <Card>
        <CardHeader>
          <CardTitle>Latency Statistics</CardTitle>
          <CardDescription>Detailed breakdown of API response times</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="h-32 flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">Minimum</p>
                <p className="text-xl font-bold">{data?.latency?.min || 0}ms</p>
              </div>
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">P50 (Median)</p>
                <p className="text-xl font-bold">{data?.latency?.p50 || 0}ms</p>
              </div>
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">P95</p>
                <p className="text-xl font-bold">{data?.latency?.p95 || 0}ms</p>
              </div>
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">P99</p>
                <p className="text-xl font-bold">{data?.latency?.p99 || 0}ms</p>
              </div>
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">Maximum</p>
                <p className="text-xl font-bold">{data?.latency?.max || 0}ms</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
